import { ReactNode, useCallback, useMemo } from 'react';
import { ConfigurationStep } from '~/app/compositions/sidebar/configuration-step/configuration-step';
import { OptionsHeader } from '~/app/compositions/sidebar/options-header';
import { BodyPaint } from '~/app/compositions/sidebar/paint/components/body-paint';
import { ContrastPaint } from '~/app/compositions/sidebar/paint/components/contrast-paint';
import { PaintTypes } from '~/app/compositions/sidebar/paint/components/paint-types';
import { StyledPaintContainer } from '~/app/compositions/sidebar/paint/styles/paint-styles';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { ImageViewsEnum } from '~/app/pages/configurator/models/image-views';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import placeholder from '~/assets/icons/colortile_not_available.svg';
import { ConfiguredCarCatalogImage } from '~/graphql/__generated__/graphql';
import { useCatalog, useConfigurator } from '~/graphql/hooks/use-configurator';
import { ExtendedCatalogImage, useImages } from '~/graphql/hooks/use-images';
import { useSteps } from '~/graphql/hooks/use-steps';

export const Paint = ({ combinations }: { combinations?: ('bodyPaint' | 'paintTypes')[] }) => {
	const { isLoading } = useConfigurator();
	const { i18n } = useI18n();
	const { config } = useMarketConfig();
	const { exteriorColor, roofColor } = useCatalog();
	const placeholderImage: ConfiguredCarCatalogImage = {
		url: placeholder,
		mimeType: 'image/svg+xml',
		__typename: 'ConfiguredCarCatalogImage',
	};

	const paintGalleryImage: ExtendedCatalogImage | null | undefined = exteriorColor?.find((a) => a.status.selected)
		?.galleryMedia
		? exteriorColor?.find((a) => a.status.selected)?.galleryMedia
		: placeholderImage;

	paintGalleryImage ? (paintGalleryImage.itemName = exteriorColor?.find((a) => a.status.selected)?.name || '') : null;

	const hasContrastColors = roofColor && roofColor?.length > 0;
	const { mediaByProfileId } = useImages();

	const getCombination = () => {
		if (!combinations) return 'paint';
		if (combinations && combinations.length > 0 && combinations.includes('bodyPaint')) return 'paint[bodyPaint]';
		if (combinations && combinations.length > 0 && combinations.includes('paintTypes')) return 'paint[paintTypes]';
		return 'paint';
	};

	const { step, total } = useSteps(getCombination());

	const media = useMemo(() => {
		if (mediaByProfileId[ImageViewsEnum.EXTERIOR_REAR_3_4] && paintGalleryImage) {
			return [[mediaByProfileId[ImageViewsEnum.EXTERIOR_REAR_3_4], paintGalleryImage]];
		}
		return [];
	}, [JSON.stringify(mediaByProfileId), paintGalleryImage]);

	const getPaint = () => {
		if (!combinations)
			return (
				<>
					<BodyPaint step={step} total={total} key="bodyPaint" />
					<PaintTypes step={step} total={total} key="paintTypes" />
					{hasContrastColors ? <ContrastPaint key="contrastPaint" /> : null}
				</>
			);

		const nodes: ReactNode[] = [];
		for (const item of combinations) {
			if (item === 'bodyPaint') nodes.push(<BodyPaint step={step} total={total} key="bodyPaint" />);
			if (item === 'bodyPaint' && hasContrastColors) nodes.push(<ContrastPaint key="contrastPaint" />);
			if (item === 'paintTypes') nodes.push(<PaintTypes step={step} total={total} key="paintTypes" />);
		}

		return nodes;
	};

	const impressionTrackingRef = useCallback(
		(node: HTMLDivElement) => {
			if (node !== null && !isLoading && total) {
				appTrackingService.selectionStepImpression({
					node,
					value: 'paint',
					elementCurrentStep: step,
					elementTotalSteps: total,
					clickID: config.hasJetStream && config.enable3d,
					element3dStreamAvailable: config.hasJetStream && config.enable3d,
				});
			}
		},
		[config.enable3d, config.hasJetStream, isLoading],
	);

	const categoryDisclaimer = config.categoryDisclaimers?.find((d) => d.step === getCombination())?.disclaimer;

	return (
		<ConfigurationStep
			ref={impressionTrackingRef}
			id={getCombination()}
			galleryType={'singleView'}
			media={media}
			galleryControls={{ avp: true, compare: true }}
			headline={
				<OptionsHeader
					id={getCombination()}
					title={i18n('ngc.equipment.bodyPaintFinishes')}
					step={step}
					total={total}
				/>
			}
			disclaimer={categoryDisclaimer}>
			<StyledPaintContainer>{getPaint()}</StyledPaintContainer>
		</ConfigurationStep>
	);
};
